import {
  useCreateExternalUrlMutation,
  useUpdateExternalUrlMutation,
} from '@carboncopyco/data';
import type { PlaybookAsset } from '@carboncopyco/types';
import { Button, InputText, Loading, Paper, notify } from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import { useClickAway } from 'ahooks';
import { Field, Form, Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { usePlaybookModalState } from '../PlaybookModalContext';
import { ErrorIndication } from './ErrorIndication';

type ExternalUrlFormProps = {
  onClose: () => void;
} & (
  | { id?: never; values?: never }
  | { id: string; values: ExternalUrlFormState }
);
type ExternalUrlFormState = {
  linkName: string;
  linkAddress: string;
};

const externalUrlSchema = object({
  linkName: string().required('This field is required'),
  linkAddress: string()
    .required('This field is required')
    .url('Please enter a valid URL'),
});
const externalUrlInitialValues = {
  linkName: '',
  linkAddress: '',
};

export const ExternalUrlForm = ({
  id,
  values,
  onClose,
}: ExternalUrlFormProps) => {
  const {
    assets,
    setAssets,
    setIsEditingExternalLink,
    playbookId,
    workspaceId,
  } = usePlaybookModalState();
  const { trackCreatePlaybookExternalUrl, trackEditPlaybookExternalUrl } =
    usePlaybookAnalytics(
      playbookId ? 'update_playbook_modal' : 'create_playbook_modal'
    );

  useEffect(() => {
    setIsEditingExternalLink.setTrue();
  }, [setIsEditingExternalLink]);

  const handleClose = () => {
    onClose();
    setIsEditingExternalLink.setFalse();
  };
  const clickAwayRef = useRef();
  useClickAway(handleClose, clickAwayRef, 'mousedown');
  const { organizationId } = useParams();
  const [createExternalUrl, { isLoading: isCreating }] =
    useCreateExternalUrlMutation();
  const [updateExternalUrl, { isLoading: isUpdating }] =
    useUpdateExternalUrlMutation();
  const isLoading = isCreating || isUpdating;
  const handleSubmit = ({ linkName, linkAddress }) => {
    values
      ? updateExternalUrl({
          organizationId,
          name: linkName,
          id,
        })
          .unwrap()
          .then((externalUrl) => {
            const externalUrlIndex = assets.findIndex(
              (asset) => asset.assetId === id
            );
            const newExternalUrl = {
              ...assets[externalUrlIndex],
              entity: externalUrl,
            };
            const newAssetArray: PlaybookAsset[] = [...assets];
            newAssetArray.splice(externalUrlIndex, 1, newExternalUrl);
            setAssets(newAssetArray);
            notify.success('Updated external link successfully');
          })
          .catch(() => {
            notify.error('Error updating an external link');
          })
          .finally(handleClose)
      : createExternalUrl({
          organizationId,
          workspaceId,
          name: linkName,
          url: linkAddress,
        })
          .unwrap()
          .then((externalUrl) => {
            const newAsset: PlaybookAsset = {
              assetId: String(externalUrl.id),
              assetRevisionId: Number(externalUrl.revisionId),
              autoSync: true,
              assetType: 'externalUrl',
              entity: externalUrl,
            };
            setAssets([...assets, newAsset]);
          })
          .catch(() => {
            notify.error('Error creating an external link');
          })
          .finally(handleClose);
  };
  return (
    <div ref={clickAwayRef}>
      <Paper
        radius
        border="ds-regular"
        padding="sm"
      >
        <Formik<ExternalUrlFormState>
          enableReinitialize={false}
          initialValues={values || externalUrlInitialValues}
          onSubmit={handleSubmit}
          validationSchema={externalUrlSchema}
        >
          {({ isValid, dirty, setTouched }) => {
            return (
              <Form>
                <div
                  className="flex flex-col gap-2"
                  onKeyDownCapture={(event) => {
                    if (event.key === 'Enter') {
                      setTouched({
                        linkName: true,
                        linkAddress: true,
                      });
                    }
                  }}
                >
                  <Field name="linkName">
                    {({ field, meta }) => {
                      const error = meta.touched && meta.error;
                      return (
                        <InputText
                          autoFocus
                          {...field}
                          placeholder="Link name"
                          disabled={isLoading}
                          state={error ? 'error' : 'normal'}
                          rightElement={
                            error ? (
                              <ErrorIndication
                                label={<div>{meta.error}</div>}
                              />
                            ) : undefined
                          }
                        />
                      );
                    }}
                  </Field>
                  <Field name="linkAddress">
                    {({ field, field: { value, ...rest }, form, meta }) => {
                      const handleExternalUrlChange = (event) => {
                        const newValue = event.target.value;
                        if (!newValue) {
                          form.setFieldValue(field.name, '');
                          return;
                        }
                        if (newValue.startsWith('https://')) {
                          form.setFieldValue(field.name, newValue);
                          return;
                        }
                        form.setFieldValue(field.name, `https://${newValue}`);
                      };

                      const error = meta.touched && meta.error;
                      return (
                        <InputText
                          {...rest}
                          value={values ? value : undefined}
                          onChange={handleExternalUrlChange}
                          placeholder="URL"
                          disabled={isLoading || !!values}
                          state={error ? 'error' : 'normal'}
                          rightElement={
                            error ? (
                              <ErrorIndication
                                label={<div>{meta.error}</div>}
                              />
                            ) : undefined
                          }
                        />
                      );
                    }}
                  </Field>
                  <div className="flex justify-end gap-2">
                    <Button
                      xSmall
                      secondary
                      label="Cancel"
                      onClick={handleClose}
                    />
                    <Button
                      xSmall
                      submit
                      onClick={() =>
                        id
                          ? trackEditPlaybookExternalUrl({
                              playbookId,
                            })
                          : trackCreatePlaybookExternalUrl({
                              playbookId,
                            })
                      }
                      disabled={!isValid || !dirty || isLoading}
                      label={!isLoading ? (values ? 'Edit' : 'Add') : undefined}
                    >
                      {isLoading ? (
                        <div className="w-12 flex items-center justify-center">
                          <Loading color="text-ds-primary-500" />
                        </div>
                      ) : undefined}
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </div>
  );
};
